import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Phone } from "lucide-react";
import { Eyebrow, Reveal, CheckItem } from "../components/ui-bits";
import { trackEvent, campaignAttribution } from "../lib/analytics";
import { measureLead } from "../lib/openaiPixel";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const PHONE = "+351 911 705 084";

const VARIANTS = {
  finance: {
    audience: "Developer",
    source: "lp-finance",
    pathTitle: "Development Finance in Portugal — Indicative Terms in Days",
    en: {
      eyebrow: "Development Finance · Portugal 🇵🇹",
      h1: "Development finance for experienced developers — indicative terms in days, not months",
      sub: "Short-term, senior-secured funding of €1m to €5m for residential projects. Land acquisition, licensing and pre-construction costs — before bank financing.",
      bullets: [
        "You hear back within 24 hours — indicative terms within days",
        "First-ranking mortgage on the project, interest + exit fee",
        "No equity taken — you keep full ownership",
        "Decided in-house by the credit team — no branch queues",
      ],
      cta: "Get indicative terms",
      formTitle: "Describe your project",
      formNote: "No obligation. A member of the credit team replies personally.",
      success: "Thank you — your enquiry has been received. A member of the credit team will come back to you within 24 hours.",
    },
    pt: {
      eyebrow: "Financiamento ao Desenvolvimento · Portugal 🇵🇹",
      h1: "Financiamento ao desenvolvimento para promotores experientes — condições indicativas em dias, não em meses",
      sub: "Financiamento de curto prazo com garantia sénior de €1m a €5m para projetos residenciais. Aquisição de terreno, licenciamento e custos de pré-construção — antes do crédito bancário.",
      bullets: [
        "Resposta em 24 horas — condições indicativas em dias",
        "Hipoteca de primeiro ranking sobre o projeto, juros + comissão de saída",
        "Sem participação no capital — mantém a totalidade da sociedade",
        "Decidido internamente pela equipa de crédito — sem filas",
      ],
      cta: "Receber condições indicativas",
      formTitle: "Descreva o seu projeto",
      formNote: "Sem compromisso. Um membro da equipa de crédito responde pessoalmente.",
      success: "Obrigado — o seu pedido foi recebido. Um membro da equipa de crédito entrará em contacto nas próximas 24 horas.",
    },
  },
  "pre-construction": {
    audience: "Developer",
    source: "lp-pre-construction",
    pathTitle: "Pre-Construction Finance Portugal — Land, Licensing, Pre-Sales",
    en: {
      eyebrow: "Pre-Construction Finance · Portugal 🇵🇹",
      h1: "Land acquired. Plans licensed. Fund the build before the bank arrives.",
      sub: "Bridge land acquisition, licensing and pre-sales with short-term senior-secured funding of €1m to €5m — then refinance with your construction bank.",
      bullets: [
        "Funds land acquisition before planning is granted",
        "Covers licensing, architecture and pre-sale marketing costs",
        "Designed for bank refinance at completion of pre-sales",
        "Indicative terms within 24 hours",
      ],
      cta: "Get indicative terms",
      formTitle: "Tell us about the project",
      formNote: "No obligation. A member of the credit team replies personally.",
      success: "Thank you — your enquiry has been received. A member of the credit team will come back to you within 24 hours.",
    },
    pt: {
      eyebrow: "Financiamento à Pré-Construção · Portugal 🇵🇹",
      h1: "Terreno adquirido. Projeto licenciado. Financie a obra antes de o banco chegar.",
      sub: "Compense a aquisição de terreno, o licenciamento e a pré-comercialização com financiamento de curto prazo com garantia sénior de €1m a €5m — depois refinancie com o seu banco.",
      bullets: [
        "Financia a aquisição do terreno antes do licenciamento",
        "Cobre licenciamento, arquitetura e comercialização prévia",
        "Desenhado para refinanciamento bancário após as pré-vendas",
        "Condições indicativas em 24 horas",
      ],
      cta: "Receber condições indicativas",
      formTitle: "Fale-nos do projeto",
      formNote: "Sem compromisso. Um membro da equipa de crédito responde pessoalmente.",
      success: "Obrigado — o seu pedido foi recebido. Um membro da equipa de crédito entrará em contacto nas próximas 24 horas.",
    },
  },
  investors: {
    audience: "Capital Partner",
    source: "lp-investors",
    pathTitle: "Lend Alongside Verde — 8% p.a. Secured on Portuguese Property",
    en: {
      eyebrow: "Invest · Portugal 🇵🇹",
      h1: "Earn 8% per annum, paid quarterly — secured on Portuguese real estate",
      sub: "Lend alongside Verde's own capital in small tickets, secured by first-ranking mortgages on residential development projects.",
      bullets: [
        "8% per annum, paid quarterly",
        "First-ranking mortgage security on the assets",
        "Small minimum tickets — lend alongside Verde's own capital",
        "Personal, transparent service from the credit team",
      ],
      cta: "Request the investor pack",
      formTitle: "Request the investor pack",
      formNote: "No obligation. We will send the pack and arrange a short call.",
      success: "Thank you — your request has been received. We will send the investor pack and come back to you shortly.",
    },
    pt: {
      eyebrow: "Investir · Portugal 🇵🇹",
      h1: "Receba 8% ao ano, pago trimestralmente — com garantia sobre imobiliário português",
      sub: "Invista ao lado do capital próprio da Verde em pequenos montantes, com garantia de hipoteca de primeiro ranking sobre projetos residenciais.",
      bullets: [
        "8% ao ano, pago trimestralmente",
        "Garantia de hipoteca de primeiro ranking sobre os ativos",
        "Pequenos montantes mínimos — ao lado do capital próprio da Verde",
        "Serviço pessoal e transparente da equipa de crédito",
      ],
      cta: "Pedir a informação para investidores",
      formTitle: "Pedir a informação para investidores",
      formNote: "Sem compromisso. Enviaremos a informação e agendaremos uma breve chamada.",
      success: "Obrigado — o seu pedido foi recebido. Enviaremos a informação para investidores e entraremos em contacto em breve.",
    },
  },
};

function LegalFooter({ lang }) {
  const t = lang === "pt"
    ? "Verde Capital — financiamento ao lado do capital próprio. Este formulário não constitui uma oferta. Ao submeter, autoriza o contacto da equipa Verde Capital sobre o seu pedido."
    : "Verde Capital — lending alongside its own capital. This form is not an offer. By submitting, you agree that the Verde Capital team may contact you about your enquiry.";
  return (
    <div className="border-t border-white/10 bg-verde-950 px-5 py-6 text-center">
      <p className="mx-auto max-w-2xl text-[12px] leading-relaxed text-white/50">{t}</p>
      <Link to="/" className="mt-2 inline-block text-[12px] font-semibold text-mint" data-testid="lp-back-home">
        {lang === "pt" ? "← Voltar ao site" : "← Back to the main site"}
      </Link>
    </div>
  );
}

export default function LandingPage({ variant: variantKey }) {
  const lang = window.location.pathname.startsWith("/pt") ? "pt" : "en";
  const v = VARIANTS[variantKey];
  const c = v[lang];
  const attribution = useMemo(() => campaignAttribution(window.location.search), []);

  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = v.pathTitle;
    let created = false;
    let m = document.querySelector('meta[name="robots"]');
    if (!m) {
      m = document.createElement("meta");
      m.name = "robots";
      created = true;
      document.head.appendChild(m);
    }
    const prev = m.content;
    m.content = "noindex, nofollow";
    trackEvent("lp_view", { variant: variantKey, lang, source: v.source });
    return () => {
      if (created) m.remove();
      else m.content = prev;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variantKey, lang]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const parts = [form.message, attribution ? `[${attribution}]` : ""].filter(Boolean).join(" ");
    try {
      const r = await fetch(`${API_BASE}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          audience: v.audience,
          name: form.name,
          email: form.email,
          phone: form.phone,
          message: parts || "Landing page enquiry.",
          source: `${v.source}${attribution ? ` | ${attribution}` : ""}`,
        }),
      });
      if (r.status !== 200) throw new Error();
      setState("done");
      trackEvent("lp_form_submit", { variant: variantKey, source: v.source });
      const data = await r.json().catch(() => ({}));
      measureLead(data.event_id);
    } catch {
      setError(lang === "pt" ? "Algo falhou — ligue-nos, por favor." : "Something went wrong — please call us instead.");
    }
  };

  const input = "mt-1.5 w-full rounded-xl border border-sand bg-white px-4 py-3 text-base text-charcoal outline-none transition-colors focus:border-verde-800";

  return (
    <div className="min-h-screen bg-cream" data-testid={`lp-${variantKey}`}>
      <div className="flex items-center justify-between bg-verde-950 px-5 py-4 sm:px-8">
        <span className="font-display text-xl font-black tracking-tight text-mint">VERDE CAPITAL</span>
        <a href={`tel:${PHONE.replace(/\s/g, "")}`} className="inline-flex items-center gap-2 text-sm font-semibold text-white" data-testid="lp-phone">
          <Phone className="h-4 w-4" /> {PHONE}
        </a>
      </div>

      <div className="mx-auto grid max-w-6xl gap-10 px-5 py-14 sm:px-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:py-20">
        <div>
          <Eyebrow>{c.eyebrow}</Eyebrow>
          <h1 className="mt-4 font-display text-4xl font-bold leading-[1.05] text-charcoal sm:text-5xl">{c.h1}</h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-fog">{c.sub}</p>
          <div className="mt-8 space-y-3" data-testid={`lp-bullets-${variantKey}`}>
            {c.bullets.map((b, i) => (
              <Reveal key={b} delay={0.05 + i * 0.05}>
                <CheckItem>{b}</CheckItem>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.1} className="w-full">
          <div className="rounded-3xl border border-sand bg-white p-6 shadow-[0_30px_60px_-30px_rgba(7,39,29,0.35)] sm:p-8" data-testid={`lp-form-${variantKey}`}>
            {state === "done" ? (
              <div className="py-8 text-center" data-testid="lp-success">
                <p className="font-display text-2xl font-bold text-verde-800">{c.cta}</p>
                <p className="mt-3 text-sm leading-relaxed text-fog">{c.success}</p>
              </div>
            ) : (
              <form onSubmit={submit}>
                <p className="font-display text-xl font-bold text-charcoal">{c.formTitle}</p>
                <label className="mt-5 block text-xs font-bold uppercase tracking-wider text-fog">
                  {lang === "pt" ? "Nome" : "Name"}
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} data-testid="lp-name" />
                </label>
                <label className="mt-4 block text-xs font-bold uppercase tracking-wider text-fog">
                  Email
                  <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} data-testid="lp-email" />
                </label>
                <label className="mt-4 block text-xs font-bold uppercase tracking-wider text-fog">
                  {lang === "pt" ? "Telefone" : "Phone"}
                  <input required type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={input} data-testid="lp-phone-input" placeholder="+351 ..." />
                </label>
                <label className="mt-4 block text-xs font-bold uppercase tracking-wider text-fog">
                  {lang === "pt" ? "Projeto (opcional)" : "Project (optional)"}
                  <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} className={input} data-testid="lp-message" />
                </label>
                {error ? <p className="mt-2 text-[13px] text-red-700" data-testid="lp-error">{error}</p> : null}
                <button
                  type="submit"
                  data-testid="lp-submit"
                  className="mt-6 w-full rounded-full bg-verde-800 px-6 py-4 text-sm font-semibold text-white transition-colors hover:bg-verde-900"
                >
                  {c.cta}
                </button>
                <p className="mt-3 text-center text-[12px] text-fog">{c.formNote}</p>
              </form>
            )}
          </div>
        </Reveal>
      </div>

      <LegalFooter lang={lang} />
    </div>
  );
}